import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/WarningAmber";
import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography,
} from "@mui/material";
import { useId, useState, ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useCase } from "../../../context/CaseContext";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { Stage } from "../../../shared/CaseConfiguration";
import { NamedEntity, OfferStatus } from "../../../shared/CaseModel";
import { signedBar, TABULAR_NUMERALS } from "../../general/dataAtoms/shared";
import { SectionLabel } from "../../general/SectionLabel";
import { TonalBadge } from "../../general/TonalBadge";
import {
  OFFER_STATUSES,
  offerLook,
  OfferStatusBadge,
  statusKey,
} from "./offerStatus";
import { showDate } from "../../timeline/TimelinePage";

/** What a card shows of an offer. */
export interface Offer {
  id: string;
  /** Its number among the offers of the two parties, in the order they were made. */
  number: number;
  // party offering
  offeror: NamedEntity;
  // receiving party
  offeree: NamedEntity;
  /** Whether the party whose page this is made the offer. */
  byParty: boolean;
  // amount to be paid
  amount: number;
  payer: NamedEntity;
  stage: Stage;
  status: OfferStatus;
  /** The day an accepted offer was accepted, where it is known. */
  acceptedOn?: string;
  /** The claims the offer settles. */
  claims: { number: number; title: string }[];
  /** The claimed amounts of those claims, and what the offer pays towards each. */
  amounts: { title: string; value: number; paid: number }[];
  fullyApportioned: boolean;
  /**
   * What the party whose page this is ends up with on average, without the
   * offer and with it. Absent where it has not been computed.
   */
  netResult?: { partyName: string; without: number; with: number };
  /** Where the two settle if another opponent settles a claimed amount of it first. */
  fallback?: ReactNode;
}

/** A claim or a claimed amount named as what it is, its title in bold. */
export const Named = ({ label, title }: { label: string; title: string }) => (
  <Trans
    i18nKey="case.analytics.settlement.named"
    values={{ label, title }}
    components={{
      quiet: <Box component="span" sx={{ color: "text.secondary" }} />,
      bold: <Box component="span" sx={{ fontWeight: 700 }} />,
    }}
  />
);

/** The height of the bars of a claimed amount and of a net result, in px. */
const AMOUNT_BAR_HEIGHT = 5;
const RESULT_BAR_HEIGHT = 12;

const SettlementOfferCard = ({
  offer,
  onStatusChange,
  onDeleteRequested,
  onEditRequested,
  onInfoRequested,
}: {
  offer: Offer;
  // a card without a callback leaves the matching action out
  onStatusChange?: (newStatus: OfferStatus) => void;
  onDeleteRequested?: () => void;
  onEditRequested?: () => void;
  onInfoRequested?: () => void;
}) => {
  const { currency } = useCurrency();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.analytics.settlement",
  });
  const menuId = useId();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // who may only read the case sees every action of the card; those that
  // change the offer are disabled, its results stay open to them
  const { readOnly } = useCase();

  const { offeror, payer, amount, status, claims, amounts, netResult } = offer;
  const look = offerLook(theme, status);
  const statusName = t(`offerDialog.status.${statusKey(status)}`);

  const delta = netResult ? netResult.with - netResult.without : 0;
  const most = netResult
    ? Math.max(Math.abs(netResult.with), Math.abs(netResult.without))
    : 0;
  const resultBar = (value: number, quiet: boolean) => (
    <Box sx={{ height: RESULT_BAR_HEIGHT }}>
      <Box
        sx={{
          width: most > 0 ? `${(Math.abs(value) / most) * 100}%` : 0,
          height: "100%",
          borderRadius: 0.75,
          ...signedBar(theme, value, quiet),
        }}
      />
    </Box>
  );

  return (
    <Paper
      component="article"
      variant="outlined"
      sx={{
        width: "100%",
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: 2,
        // a draft has not left the party yet
        borderStyle: status === OfferStatus.Draft ? "dashed" : "solid",
        borderLeft: `5px solid ${look.color}`,
        userSelect: "none",
      }}
    >
      {/* whose offer it is, what it offers and where it stands */}
      <Box sx={{ bgcolor: look.wash, pl: 1.75, pr: 1.5, pt: 1.25, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.25,
          }}
        >
          {onStatusChange ? (
            // the status is said once, and is changed from here
            <>
              <ButtonBase
                aria-haspopup="menu"
                aria-controls={menuAnchor ? menuId : undefined}
                aria-expanded={menuAnchor ? true : undefined}
                aria-label={t("card.status", { status: statusName })}
                disabled={readOnly}
                onClick={(event) => setMenuAnchor(event.currentTarget)}
                sx={{
                  // a bare button has no look of its own for being disabled
                  "&.Mui-disabled": {
                    opacity: theme.palette.action.disabledOpacity,
                  },
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  pl: 0.875,
                  pr: 0.625,
                  py: 0.375,
                  borderRadius: 999,
                  bgcolor: look.pill,
                  color: look.ink,
                  typography: "caption",
                  fontWeight: 700,
                }}
              >
                <look.Icon sx={{ fontSize: 15 }} />
                {statusName}
                <ExpandIcon sx={{ fontSize: 16 }} />
              </ButtonBase>
              <Menu
                id={menuId}
                anchorEl={menuAnchor}
                open={menuAnchor !== null}
                onClose={() => setMenuAnchor(null)}
                slotProps={{ list: { "aria-label": t("card.statusMenu") } }}
              >
                {OFFER_STATUSES.map((option) => (
                  <MenuItem
                    key={option}
                    selected={option === status}
                    onClick={() => {
                      setMenuAnchor(null);
                      if (option !== status) onStatusChange(option);
                    }}
                    sx={{ minWidth: 190 }}
                  >
                    <ListItemIcon sx={{ minWidth: 26 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          bgcolor: offerLook(theme, option).color,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText>
                      {t(`offerDialog.status.${statusKey(option)}`)}
                    </ListItemText>
                    {option === status && <CheckIcon fontSize="small" />}
                  </MenuItem>
                ))}
              </Menu>
            </>
          ) : (
            <OfferStatusBadge status={status} />
          )}
          {status === OfferStatus.Accepted && offer.acceptedOn && (
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mr: "auto" }}
            >
              {t("card.acceptedOn", { date: showDate(offer.acceptedOn) })}
            </Typography>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {onInfoRequested && (
              <Button
                size="small"
                onClick={onInfoRequested}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {t("card.results")}
              </Button>
            )}
            {onEditRequested && (
              <IconButton
                size="small"
                aria-label={t("card.edit")}
                title={t("card.edit")}
                disabled={readOnly}
                onClick={onEditRequested}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {onDeleteRequested && (
              <IconButton
                size="small"
                aria-label={t("card.delete")}
                title={t("card.delete")}
                disabled={readOnly}
                onClick={onDeleteRequested}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
          {t("card.offerBy", { name: offeror.name })}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            columnGap: 1.125,
            flexWrap: "wrap",
          }}
        >
          <Typography
            variant="h4"
            component="p"
            sx={{
              fontWeight: 700,
              letterSpacing: "-.02em",
              lineHeight: 1.2,
              ...TABULAR_NUMERALS,
            }}
          >
            {currency.format(amount)}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {t("card.paidBy", { name: payer.name })}
          </Typography>
        </Box>
      </Box>

      {/* what the offer settles, and what it pays towards each claimed amount */}
      <Box
        sx={{
          pl: 1.75,
          pr: 1.5,
          pt: 1.375,
          pb: 1.5,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <SectionLabel component="h3">{t("card.settles")}</SectionLabel>
        {claims.length === 0 && (
          <Typography
            variant="body2"
            sx={{ mt: 0.25, color: theme.palette.data.negative.dark }}
          >
            {t("card.noClaims")}
          </Typography>
        )}
        {claims.map((claim) => (
          <Typography
            key={claim.number}
            variant="body2"
            sx={{ mt: 0.25, fontWeight: 600 }}
          >
            <Named
              label={t("number", {
                keyPrefix: "case.claims",
                ordinal: claim.number,
              })}
              title={claim.title}
            />
          </Typography>
        ))}
        {amounts.map(({ title, value, paid }) => (
          <Box
            key={title}
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              columnGap: 1.5,
              rowGap: 0.375,
              mt: 0.875,
            }}
          >
            <Typography variant="caption">
              <Named label={t("claimedAmount")} title={title} />
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", ...TABULAR_NUMERALS }}
            >
              <Trans
                i18nKey="case.analytics.settlement.card.paidOf"
                values={{
                  paid: currency.format(paid),
                  claimed: currency.format(value),
                }}
                components={{
                  bold: (
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: "text.primary" }}
                    />
                  ),
                }}
              />
            </Typography>
            <Box
              sx={{
                gridColumn: "1 / -1",
                height: AMOUNT_BAR_HEIGHT,
                borderRadius: 0.75,
                bgcolor: "action.hover",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: value > 0 ? `${Math.min(1, paid / value) * 100}%` : 0,
                  height: "100%",
                  bgcolor: "primary.light",
                }}
              />
            </Box>
          </Box>
        ))}
        {!offer.fullyApportioned && (
          <TonalBadge tone={theme.palette.warning} sx={{ mt: 1.25 }}>
            <WarningIcon sx={{ fontSize: 14 }} />
            {t("card.notApportioned")}
          </TonalBadge>
        )}
      </Box>

      {/* what the offer does to the party's net result */}
      {netResult && (
        <Box
          sx={{
            pl: 1.75,
            pr: 1.5,
            pt: 1.375,
            pb: 1.5,
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.25,
            }}
          >
            <SectionLabel component="h3">
              {t("card.netResult", { name: netResult.partyName })}
            </SectionLabel>
            <TonalBadge
              tone={
                delta > 0
                  ? theme.palette.data.positive
                  : delta < 0
                    ? theme.palette.data.negative
                    : undefined
              }
              sx={TABULAR_NUMERALS}
            >
              {delta > 0
                ? `+${currency.format(delta)}`
                : currency.format(delta)}
            </TonalBadge>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto minmax(0, 1fr) auto",
              columnGap: 1.25,
              rowGap: 0.75,
              alignItems: "center",
              mt: 1,
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("card.without")}
            </Typography>
            {resultBar(netResult.without, true)}
            <Typography
              variant="caption"
              sx={{ textAlign: "right", ...TABULAR_NUMERALS }}
            >
              {currency.format(netResult.without)}
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {t("card.with")}
            </Typography>
            {resultBar(netResult.with, false)}
            <Typography
              variant="caption"
              sx={{ textAlign: "right", fontWeight: 700, ...TABULAR_NUMERALS }}
            >
              {currency.format(netResult.with)}
            </Typography>
          </Box>
        </Box>
      )}
      {offer.fallback && (
        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}>
          {offer.fallback}
        </Box>
      )}
    </Paper>
  );
};

export default SettlementOfferCard;
